import { useMemo, useRef } from 'react';
import { getIsDateTime, getIsDate, getIsTime } from '../utils/typeGuards.js';
import { dateTimeToTime, dateTimeToDate } from '../utils/stringHelpers.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';

export const useRelevantValueFromProps = (props: DSControlledDateTimePickerT.InternalProps) => {
  const isDateTime = getIsDateTime(props);
  const isDate = getIsDate(props);
  const isTime = getIsTime(props);
  const { dateTime } = props;
  // why not destructuring? because destructuring will not trigger typescript safeguards infers.
  // this is safer and more explicit anyway, when things are undefined we default to '' which is later used in ternaries as a single check
  const time = isTime ? props.time : '';
  const date = isDate ? props.date : '';

  const tempDateStringRef = useRef('__/__/____');
  const dateStringFromProps = useMemo(() => {
    if (isDateTime)
      tempDateStringRef.current = dateTime !== '' && dateTime !== undefined ? dateTimeToDate(dateTime) : '__/__/____';
    if (isDate) tempDateStringRef.current = date !== '' ? date : '__/__/____';
    // the ref is storing a primitive value, which means that even if we return the .current, it will trigger shallow comparisons
    // don't do the same with non primitive values, instead do a {...ref.current} or [...ref.current] to ensure a new reference is created on memo run
    // which in turns would trigger sideEffects as expected on ref change.
    //   note: side-effects doesn't run on non primitive useRef().current changes.
    return tempDateStringRef.current;
  }, [isDate, isDateTime, date, dateTime]);

  const tempTimeStringRef = useRef('__:__ __');
  const timeStringFromProps = useMemo(() => {
    if (isDateTime)
      tempTimeStringRef.current = dateTime !== '' && dateTime !== undefined ? dateTimeToTime(dateTime) : '__:__ __';
    if (isTime) tempTimeStringRef.current = time !== '' ? time : '__:__ __';
    // the ref is storing a primitive value, which means that even if we return the .current, it will trigger shallow comparisons
    // don't do the same with non primitive values, instead do a {...ref.current} or [...ref.current] to ensure a new reference is created on memo run
    // which in turns would trigger sideEffects as expected on ref change.
    //   note: side-effects doesn't run on non primitive useRef().current changes.
    return tempTimeStringRef.current;
  }, [isDateTime, isTime, time, dateTime]);

  return useMemo(
    () => ({
      dateStringFromProps,
      timeStringFromProps,
      tempDateStringRef,
      tempTimeStringRef,
    }),
    [dateStringFromProps, timeStringFromProps],
  );
};
